// Resource view: full talent catalog as rows, team columns (with
// team-change overlays for hires / departures / upskills) plus per-project
// requirement columns on the right. Answers "what does the team look like
// under this roadmap?" — every visible skill stays on screen so changes
// (overlay rows, hires, departures, upskills) read as highlights rather
// than as a filtered subset.

import type { Engineer } from '@lib/api-types/engineers';
import type { RatingValue } from '@lib/api-types/ratings';
import { Fragment, useMemo, useState, type CSSProperties } from 'react';

import { EmptyState } from '../../components/EmptyState.tsx';
import {
  GroupedCompLabel,
  NestConnector,
  PropIndicator,
  nestVariant,
} from '../../components/TruncatedLabel.tsx';
import {
  RATING_LABELS,
  buildSkillFunctions,
  compRowHeight,
  isCompactCatalog,
  isInstitutional,
} from '../../data-views.ts';
import type {
  MergedDerivedContext,
  ProjectView,
  ProjectedTalentColumn,
  RoadmapProjectionContext,
} from '../../roadmap-data.ts';


import { EditableLabel } from './EditableLabel.tsx';
import { OverlayPicker, type OverlayOption } from './OverlayPicker.tsx';
import { reqKey } from './req-key.ts';
import {
  RequirementCell,
  type RequirementValue,
} from './RequirementCell.tsx';
import { ResourceProjectHeader } from './ResourceProjectHeader.tsx';
import { TeamChangeColumnHeader } from './TeamChangeColumnHeader.tsx';
import { TeamChangeRatingCell } from './TeamChangeRatingCell.tsx';

type TalentColumn = Engineer | ProjectedTalentColumn;

const RATING_LETTERS: Record<RatingValue, string> = {
  1: 'A',
  2: 'C',
  3: 'P',
  4: 'E',
};

/** Color values for the green talent ramp — kept in lockstep with the
 *  `.snap-cell.green.lvl-*` rules in tokens.css. Used to drive the
 *  upskill cell's animated fade between `from` and `to` levels. */
const GREEN_RAMP: Record<number, string> = {
  0: '#f6f6f6',
  1: '#dbf5cd',
  2: '#a3f379',
  3: '#54d212',
  4: '#3bab00',
};

function getKind(eng: TalentColumn): 'human' | 'agent' {
  return 'kind' in eng && eng.kind === 'agent' ? 'agent' : 'human';
}

function isProjectedHire(eng: TalentColumn): boolean {
  return 'is_new' in eng && eng.is_new === true;
}

function formatShortName(display: string): string {
  const parts = display.trim().split(/\s+/);
  if (parts.length < 2) return display;
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  const lastInitial = last[0]?.toUpperCase() ?? '';
  return lastInitial ? `${first} ${lastInitial}.` : first;
}

export function RoadmapResourceGrid({
  project,
  ctx,
  functionsCount,
  grouped,
  createMode = false,
  teamChangeMode = false,
  onAddProfile,
  onAddIC,
  onAddDerivedContext,
  ratingOverrides,
  onSetRating,
  addedICIDs,
  departedICIDs,
  onRenameIC,
  onDeleteIC,
  engineerSkillFunctions,
  onToggleEngineerSkillFunction,
  onRenameRole,
  onRenameDerivedContext,
  onDeleteDerivedContext,
  deprecatedSkillIds,
  onDeprecateDerivedContext,
  onRestoreDerivedContext,
  onRestoreSkill,
  onRenameProject,
  onSetRequirement,
  deprecatedMap,
  onAddSkill,
  onRenameSkill,
  onDeleteSkill,
  onDeleteRole,
  availableFunctions,
  skillFunctions,
  onToggleSkillFunction,
  overlayCompsPool,
  overlaySkillsPool,
  linkedCompCounts,
  linkedSkillCounts,
}: {
  project: ProjectView | null;
  ctx: RoadmapProjectionContext;
  functionsCount: number;
  grouped: boolean;
  /** Create-mode tweaks: double-width role columns + a trailing ghost column. */
  createMode?: boolean;
  /** Team-change-mode tweaks: ghost column after team columns + ghost row. */
  teamChangeMode?: boolean;
  /** Click handler for the ghost "+" column. Required only when `createMode`. */
  onAddProfile?: () => void;
  /** Click handler for the ghost "+" column in team-change mode (adds an IC). */
  onAddIC?: () => void;
  /** Team-change mode: per-(engineer, skill) rating override map. */
  ratingOverrides?: Record<string, Record<string, RatingValue>>;
  /** Set / clear an override. `null` reverts to the baseline rating. */
  onSetRating?: (
    engineerId: string,
    skillId: string,
    next: RatingValue | null
  ) => void;
  /** IDs of ICs added in this session (vs existing from snapshot). */
  addedICIDs?: Set<string>;
  /** Existing ICs currently flagged as departed for this session. */
  departedICIDs?: Set<string>;
  /** Rename an IC's display (added or existing). */
  onRenameIC?: (id: string, next: string) => void;
  /** Trash click: caller decides remove vs. mark-departed based on
   *  whether the id is in `addedICIDs`. */
  onDeleteIC?: (id: string) => void;
  /** Team-change: per-(engineer, skill) function assignment overrides.
   *  Absent entry = "all comp.functions" (the implicit default). */
  engineerSkillFunctions?: Record<string, Record<string, string[]>>;
  /** Toggle a single function for the (engineer, skill) pair. */
  onToggleEngineerSkillFunction?: (
    engineerId: string,
    skillId: string,
    fnId: string,
    defaultIds: string[]
  ) => void;
  /** Add-DerivedContext handler — receives the OverlayPicker's choice.
   *  When `id` is set the caller must REUSE that id so the new plan
   *  links to the same overlay as whatever plan introduced it. When
   *  `id` is undefined the caller mints a fresh id from `display`. */
  onAddDerivedContext?: (item: { id?: string; display: string }) => void;
  /** Roadmap-draft pool of overlay derivedContexts surfaced as picker
   *  suggestions. Populated from currently-referenced plans in the
   *  active roadmap session minus anything already added to the open
   *  scaffold (those aren't candidates for re-adding). */
  overlayCompsPool?: OverlayOption[];
  /** Per-comp pool of overlay skills sourced from the same plans as
   *  `overlayCompsPool`. Surfaced by the per-comp Add Skill picker. */
  overlaySkillsPool?: Record<string, OverlayOption[]>;
  /** Per-id count of OTHER plans in the active roadmap that reference
   *  this overlay comp / skill. Drives the "linked to N plans"
   *  warning indicator on EditableLabel so the user knows a rename
   *  here syncs across the linked set. */
  linkedCompCounts?: Map<string, number>;
  /** Same idea for overlay skills, nested under their comp id. */
  linkedSkillCounts?: Map<string, Map<string, number>>;
  /** Create-mode hook: makes role labels editable in the project banner. */
  onRenameRole?: (roleId: string, next: string) => void;
  /** Hook: makes derivedContext labels editable (only overlay comps — i.e.
   *  the ones added in this session). Wired by both create-project and
   *  team-change flows. */
  onRenameDerivedContext?: (compId: string, next: string) => void;
  /** Hook: deletes an added derivedContext. Trash icon appears alongside
   *  the pencil on overlay comp labels. */
  onDeleteDerivedContext?: (compId: string) => void;
  /** Set of skill ids fully deprecated across every role. Drives the
   *  strikethrough state on skill + comp labels. */
  deprecatedSkillIds?: Set<string>;
  /** Click → mark every (role, skill) under the comp as deprecated. */
  onDeprecateDerivedContext?: (compId: string, skillIds: string[]) => void;
  /** Click → clear deprecation for every (role, skill) under the comp. */
  onRestoreDerivedContext?: (compId: string, skillIds: string[]) => void;
  /** Click → clear deprecation for every (role, skill) on this skill. */
  onRestoreSkill?: (compId: string, skillId: string) => void;
  /** Create-mode hook: makes the project name editable in the banner. */
  onRenameProject?: (next: string) => void;
  /** Create-mode hook: per-(role, derivedContext, skill) requirement picker.
   *  Scoped by derivedContext so a skill shared across derivedContexts can carry
   *  an independent bar in each. `'deprecated'` paints the cell with the
   *  snapshot-wide stripe pattern (same as Timeline's removed items). */
  onSetRequirement?: (
    roleId: string,
    compId: string,
    skillId: string,
    level: RequirementValue
  ) => void;
  /** Create-mode: outer key roleId, inner key reqKey(comp, skill) → true if
   *  marked deprecated. Read alongside `requirementsByRole` to render. */
  deprecatedMap?: Record<string, Record<string, true>>;
  /** Add-Skill handler — receives the OverlayPicker's choice + the
   *  target comp. Same id-reuse vs mint-new semantics as
   *  `onAddDerivedContext`. */
  onAddSkill?: (compId: string, item: { id?: string; display: string }) => void;
  /** Create-mode hook: renames an added skill (flat-mode label edit). */
  onRenameSkill?: (compId: string, skillId: string, next: string) => void;
  /** Create-mode hook: deletes an added skill. */
  onDeleteSkill?: (compId: string, skillId: string) => void;
  /** Create-mode hook: deletes a role; trash hidden on the last role. */
  onDeleteRole?: (roleId: string) => void;
  /** Full BF list shown in the "Assign functions" picker sub-view. */
  availableFunctions?: Array<{ id: string; display: string }>;
  /** Lookup of explicit per-(role, comp::skill) function assignments — when
   *  absent, falls back to "all comp.functions" (impact area 1). */
  skillFunctions?: Record<string, Record<string, string[]>>;
  /** Toggle one function on/off for a specific (role, derivedContext, skill).
   *  The defaultIds is forwarded so the handler can seed the explicit
   *  override from the default set on first toggle. */
  onToggleSkillFunction?: (
    roleId: string,
    compId: string,
    skillId: string,
    fnId: string,
    defaultIds: string[]
  ) => void;
}) {
  // Hooks run before any early return so the call order stays stable
  // across renders (react-hooks/rules-of-hooks). `project` is the
  // synthetic talent ProjectView from the RoadmapView dispatcher; if
  // it's null we render the empty state at the bottom.
  // useMemo wrappers keep the empty-default references stable across
  // renders so downstream useMemos can cache.
  // OverlayPicker open state. Mutually exclusive: either the bottom
  // "Add Context" picker is open or one comp's "Add Skill" picker
  // is. Both render in the ghost-row slot at the bottom of the grid
  // so the layout doesn't have to shift to accommodate them.
  const [picker, setPicker] = useState<
    | { kind: 'comp' }
    | { kind: 'skill'; compId: string; compDisplay: string }
    | null
  >(null);
  const mergedDerivedContexts = useMemo(
    () => project?.mergedDerivedContexts ?? [],
    [project]
  );
  // Skill → its catalog function footprint (union across every derivedContext
  // that lists the skill). Drives the default-selected functions when a
  // skill/derivedContext is added, so the project's intersections match the
  // Resilience platform map rather than the derivedContext's narrow functions.
  const catalogSkillFns = useMemo(
    () => buildSkillFunctions(mergedDerivedContexts),
    [mergedDerivedContexts]
  );
  const maxRequirements = useMemo(
    () => project?.maxRequirements ?? {},
    [project]
  );
  const projectGroups = useMemo(
    () => project?.projectGroups ?? [],
    [project]
  );

  // When any project is toggled on, the Resource view zooms in on the
  // demand side: show ONLY the project / role columns and the talent
  // catalog as rows. Engineer / agent / departed columns and the
  // team-change overlays disappear so the matrix reads as "what does
  // the work require, mapped to which skills." With no projects on, the
  // talent map + team changes reappear (this is the live-team view).
  const anyProjectOn = projectGroups.length > 0;

  const humans = useMemo(
    () =>
      anyProjectOn ? [] : ctx.teamColumns.filter(e => getKind(e) === 'human'),
    [ctx.teamColumns, anyProjectOn]
  );
  const agents = useMemo(
    () =>
      anyProjectOn ? [] : ctx.teamColumns.filter(e => getKind(e) === 'agent'),
    [ctx.teamColumns, anyProjectOn]
  );
  const departed = anyProjectOn ? [] : ctx.departedHumans;

  // Build the dynamic grid template + col-index lookups in one pass so
  // every cell can be placed via `gridColumn` without re-counting. In
  // `createMode` the role columns double (48 → 96px) for breathing room
  // and a trailing 96px ghost column is appended for "add profile".
  const roleColPx = createMode ? 96 : 48;
  const layout = useMemo(() => {
    // DerivedContext column is 130px to match the other matrices; the
    // create-project / team-change flows widen it to 160px so longer
    // renames and the hover affordances have room without truncating.
    const compColPx = createMode || teamChangeMode ? 160 : 130;
    const cols: string[] = [`${compColPx}px`, '42px', '8px', '140px'];
    let cursor = 5; // grid columns are 1-indexed; left labels occupy 1-4
    const humanCols: number[] = [];
    const agentCols: number[] = [];
    const departedCols: number[] = [];
    const groupRoleCols: number[][] = [];
    let ghostCol: number | null = null;

    if (humans.length > 0) {
      cols.push(`repeat(${humans.length}, 36px)`);
      for (let i = 0; i < humans.length; i++) humanCols.push(cursor++);
    }
    if (agents.length > 0) {
      cols.push('24px');
      cursor++; // spacer column
      cols.push(`repeat(${agents.length}, 36px)`);
      for (let i = 0; i < agents.length; i++) agentCols.push(cursor++);
    }
    if (departed.length > 0) {
      cols.push('24px');
      cursor++; // spacer column
      cols.push(`repeat(${departed.length}, 36px)`);
      for (let i = 0; i < departed.length; i++) departedCols.push(cursor++);
    }
    if (teamChangeMode) {
      // Team-change ghost lives at the trailing end of the team-column
      // strip so "+ Add IC" extends the same horizontal band the rest
      // of the humans / agents / departed occupy. Width is generous
      // (96px) so the dashed-button label fits on a single line; the
      // narrower 36px team columns continue to live to its left.
      cols.push('12px');
      cursor++; // gap before ghost
      cols.push('96px');
      ghostCol = cursor++;
    }
    if (projectGroups.length > 0) {
      cols.push('24px');
      cursor++; // pre-projects spacer
      projectGroups.forEach((g, gi) => {
        if (gi > 0) {
          cols.push('12px');
          cursor++; // inter-group gap
        }
        cols.push(`repeat(${g.roles.length}, ${roleColPx}px)`);
        const groupCols: number[] = [];
        for (let i = 0; i < g.roles.length; i++) groupCols.push(cursor++);
        groupRoleCols.push(groupCols);
      });
    }
    if (createMode) {
      // Ghost "+" column tails the role columns. Sits at the same role
      // column width so it visually previews where the next profile
      // would land.
      cols.push('12px');
      cursor++; // gap before ghost
      cols.push(`${roleColPx}px`);
      ghostCol = cursor++;
    }
    // Trailing filler track: eats remaining horizontal space so the
    // fixed-width engineer / agent / departed / project columns hug
    // the LEFT of the grid. Ratio is `${Nfn}fr` (matching Talent's
    // trailing) so the skill column ends up at 1/(1+Nfn) of free
    // space — identical to the share Resilience / Timeline /
    // Roadmap-Impact give it via `1fr` + `repeat(N, minmax(72px,
    // 1fr))`. Falls back to `1fr` so the layout still renders when a
    // snapshot ships no functions.
    cols.push(`${Math.max(1, functionsCount)}fr`);

    // Cumulative left offsets for the four frozen label columns (widths
    // compColPx/42/8/150 + the 6px grid gap) so they pin to the left edge
    // while the people / role columns scroll horizontally.
    const freezeLefts = [0, compColPx + 6, compColPx + 54, compColPx + 68];

    return {
      gridTemplateColumns: cols.join(' '),
      humanCols,
      agentCols,
      departedCols,
      groupRoleCols,
      ghostCol,
      freezeLefts,
    };
  }, [humans.length, agents.length, departed.length, projectGroups, functionsCount, createMode, roleColPx, teamChangeMode]);

  const visibleComps = useMemo(
    () => mergedDerivedContexts.filter(c => c.skills.length > 0),
    [mergedDerivedContexts]
  );

  // Last row that holds real content. Used to:
  //   - end the ghost "+" column just before the trailing ghost row
  //   - place the bottom ghost "+ context" row directly below it
  // Grouped: 1 header + one row per derivedContext. Flat: 1 header + one
  // row per skill (skill rows are placed implicitly, but indices are
  // still deterministic).
  const lastContentRow = grouped
    ? 1 + visibleComps.length
    : 1 + visibleComps.reduce((s, c) => s + c.skills.length, 0);
  const ghostRowIdx = lastContentRow + 1;

  // Single 80px header row. In project mode, each ResourceProjectHeader
  // internally uses subgrid to lay out the project name (top) over its
  // role labels (bottom) — banner + roles read as one cohesive header
  // block per project. Engineer / agent / departed headers also live in
  // this same 80px row (their slanted name track is sized to fit — the
  // height clears longer names against the sticky box top; keep in sync
  // with `.snap-grid--resource .snap-grid__col-header` in tokens.css).
  // The bottom "+ context" ghost row in create mode is content-sized
  // via min-height on the button (not an explicit track) so the same
  // logic works for grouped (explicit per-comp tracks) and flat
  // (implicit per-skill tracks) modes alike.
  const gridStyle = useMemo(() => {
    if (!grouped) {
      return {
        gridTemplateColumns: layout.gridTemplateColumns,
        gridTemplateRows: '80px',
      };
    }
    const compact = isCompactCatalog(mergedDerivedContexts);
    const heights = visibleComps
      .map(c => `${compRowHeight(c.skills.length, compact)}px`)
      .join(' ');
    return {
      gridTemplateColumns: layout.gridTemplateColumns,
      gridTemplateRows: `80px ${heights}`,
    };
  }, [layout.gridTemplateColumns, grouped, visibleComps, mergedDerivedContexts]);

  const hasAnyColumns =
    humans.length + agents.length + departed.length + projectGroups.length > 0;

  if (!project) {
    return (
      <p className='snap-subtitle'>
        Pick a project or roadmap to see the team in context.
      </p>
    );
  }

  if (visibleComps.length === 0) {
    return (
      <EmptyState
        title='No visible skills'
        description='Add framework, service, or institutional skills to populate the talent catalog.'
      />
    );
  }

  // Engineer column headers get a left→right *descending* z-index (first
  // column highest) so each rotated name, which overflows up-and-right
  // into the next column, paints over that column's opaque header
  // background instead of being clipped. Order matches render: humans,
  // then agents, then departed. Stays under the frozen corner (z 200).
  const totalEngCols = humans.length + agents.length + departed.length;
  const agentZBase = totalEngCols - humans.length;
  const departedZBase = agentZBase - agents.length;

  return (
    <div className='snap-matrix-scroll'>
    <div className='snap-grid snap-grid--resource' style={gridStyle}>
      {/* ---- Row 1: unified 80px header zone ----
       * Left labels (rows + nest connectors etc.) get a placeholder.
       * Engineer / agent / departed headers live in row 1 with their
       * 80px slanted-name tracks. Project groups render a single
       * ResourceProjectHeader per group: project name on top, role
       * labels on bottom — one cohesive block per project (no row gap
       * between banner and roles). */}
      <div
        className='snap-grid__freeze-corner is-frozen is-frozen-edge'
        style={{ gridRow: 1, gridColumn: '1 / span 4', left: 0 }}
      />
      {humans.map((eng, i) =>
        teamChangeMode && onRenameIC && onDeleteIC ? (
          <TeamChangeColumnHeader
            key={`hd-h-${eng.id}`}
            display={eng.display_name}
            col={layout.humanCols[i]!}
            row={1}
            zIndex={totalEngCols - i}
            isAgent={false}
            isHire={isProjectedHire(eng)}
            isAdded={addedICIDs?.has(eng.id) ?? false}
            isDeparted={departedICIDs?.has(eng.id) ?? false}
            onRename={next => onRenameIC(eng.id, next)}
            onDelete={() => onDeleteIC(eng.id)}
          />
        ) : (
          <ResourceColHeader
            key={`hd-h-${eng.id}`}
            eng={eng}
            col={layout.humanCols[i]!}
            row={1}
            zIndex={totalEngCols - i}
          />
        )
      )}
      {agents.map((eng, i) =>
        teamChangeMode && onRenameIC && onDeleteIC ? (
          <TeamChangeColumnHeader
            key={`hd-a-${eng.id}`}
            display={eng.display_name}
            col={layout.agentCols[i]!}
            row={1}
            zIndex={agentZBase - i}
            isAgent
            isHire={isProjectedHire(eng)}
            isAdded={addedICIDs?.has(eng.id) ?? false}
            isDeparted={departedICIDs?.has(eng.id) ?? false}
            onRename={next => onRenameIC(eng.id, next)}
            onDelete={() => onDeleteIC(eng.id)}
          />
        ) : (
          <ResourceColHeader
            key={`hd-a-${eng.id}`}
            eng={eng}
            col={layout.agentCols[i]!}
            row={1}
            zIndex={agentZBase - i}
          />
        )
      )}
      {departed.map((eng, i) => (
        <div
          key={`hd-d-${eng.id}`}
          className='snap-grid__col-header snap-grid__col-header--departed'
          style={{ gridRow: 1, gridColumn: layout.departedCols[i], zIndex: departedZBase - i }}
        >
          <strong>{formatShortName(eng.display_name)}</strong>
        </div>
      ))}
      {projectGroups.map((g, gi) => {
        const cols = layout.groupRoleCols[gi];
        if (!cols || cols.length === 0) return null;
        return (
          <ResourceProjectHeader
            key={`hd-p-${g.id}`}
            display={g.display}
            roles={g.roles}
            startCol={cols[0]!}
            onRenameRole={createMode ? onRenameRole : undefined}
            onRenameProject={createMode ? onRenameProject : undefined}
            onDeleteRole={createMode ? onDeleteRole : undefined}
          />
        );
      })}
      {(createMode || teamChangeMode) && layout.ghostCol != null ? (
        <>
          <button
            type='button'
            className='snap-grid__ghost-column'
            // Starts at the top of the first content row (row 2) so the
            // dashed box sits flush with the skill grid rather than
            // intruding into the project banner. Ends just above the
            // bottom ghost row so the two affordances form an L-shape
            // rather than overlap.
            style={{
              gridRow: `2 / ${ghostRowIdx}`,
              gridColumn: layout.ghostCol,
            }}
            onClick={teamChangeMode ? onAddIC : onAddProfile}
          >
            <span className='snap-grid__ghost-label'>
              {teamChangeMode ? '+ Add IC' : '+ Add Role'}
            </span>
          </button>
          {picker ? (
            <div
              style={{
                gridRow: ghostRowIdx,
                gridColumn: `1 / ${layout.ghostCol - 1}`,
              }}
            >
              {picker.kind === 'comp' ? (
                <OverlayPicker
                  options={overlayCompsPool ?? []}
                  placeholder='Context name…'
                  createLabel='Create context'
                  onPick={opt => {
                    onAddDerivedContext?.({ id: opt.id, display: opt.display });
                    setPicker(null);
                  }}
                  onCreate={display => {
                    onAddDerivedContext?.({ display });
                    setPicker(null);
                  }}
                  onClose={() => setPicker(null)}
                />
              ) : (
                <OverlayPicker
                  header={`Add skill to ${picker.compDisplay}`}
                  options={overlaySkillsPool?.[picker.compId] ?? []}
                  placeholder='Skill name…'
                  createLabel='Create skill'
                  onPick={opt => {
                    onAddSkill?.(picker.compId, {
                      id: opt.id,
                      display: opt.display,
                    });
                    setPicker(null);
                  }}
                  onCreate={display => {
                    onAddSkill?.(picker.compId, { display });
                    setPicker(null);
                  }}
                  onClose={() => setPicker(null)}
                />
              )}
            </div>
          ) : (
            <button
              type='button'
              className='snap-grid__ghost-row'
              // Spans the derivedContext / skill / proficiency label columns
              // (1–4) through the last data column — stopping one column
              // short of the ghost "+" column gap so the row and column
              // affordances don't overlap (per spec).
              style={{
                gridRow: ghostRowIdx,
                gridColumn: `1 / ${layout.ghostCol - 1}`,
              }}
              onClick={() => setPicker({ kind: 'comp' })}
            >
              <span className='snap-grid__ghost-label'>+ Add Context</span>
            </button>
          )}
        </>
      ) : null}

      {/* ---- Rows 2+: per-skill or per-comp rows ---- */}
      {/*
        Every child below carries an explicit `gridRow`. Without it,
        CSS Grid auto-placement walks row-by-row looking for the first
        empty cell — and since row 1 has only the project banners on
        the right, the first skill row's engineer cells would backfill
        into row 1 above the column headers (the regression that
        prompted this fix).
      */}
      {grouped
        ? visibleComps.map((comp, compIdx) => {
            const rowIdx = 2 + compIdx;
            const compSkillIds = comp.skills.map(s => s.id);
            const isCompDeprecated =
              compSkillIds.length > 0 &&
              compSkillIds.every(id =>
                deprecatedSkillIds?.has(reqKey(comp.id, id))
              );
            // Deprecate icon only on default (non-overlay) comps that
            // aren't already deprecated — overlay comps get a trash,
            // deprecated comps get a restore.
            const canDeprecate =
              !comp.is_overlay &&
              !isCompDeprecated &&
              onDeprecateDerivedContext != null;
            const canRestore = isCompDeprecated && onRestoreDerivedContext != null;
            // Surface added-skill count in blue so the user gets visible
            // feedback that clicking "+" worked — important in grouped
            // mode where the new skill row isn't visible (it's collapsed
            // into the comp aggregate cell).
            const addedSkills = createMode
              ? comp.skills.filter(s => s.is_overlay).length
              : 0;
            return (
              <Fragment key={comp.id}>
                <GroupedCompLabel
                  frozen
                  freezeLefts={layout.freezeLefts}
                  gridRow={rowIdx}
                  propCount={comp.skills.filter(isInstitutional).length}
                  name={
                    <EditableLabel
                      text={comp.display}
                      truncMax={22}
                      isOverlay={comp.is_overlay}
                      isDeprecated={isCompDeprecated}
                      linkedPlansCount={
                        comp.is_overlay
                          ? linkedCompCounts?.get(comp.id)
                          : undefined
                      }
                      linkedNoun='derived_context'
                      onSave={
                        onRenameDerivedContext && comp.is_overlay
                          ? next => onRenameDerivedContext(comp.id, next)
                          : undefined
                      }
                      onDelete={
                        onDeleteDerivedContext && comp.is_overlay
                          ? () => onDeleteDerivedContext(comp.id)
                          : undefined
                      }
                      onDeprecate={
                        canDeprecate
                          ? () => onDeprecateDerivedContext!(comp.id, compSkillIds)
                          : undefined
                      }
                      onRestore={
                        canRestore
                          ? () => onRestoreDerivedContext!(comp.id, compSkillIds)
                          : undefined
                      }
                    />
                  }
                  count={
                    <>
                      {comp.skills.length} skill
                      {comp.skills.length === 1 ? '' : 's'}
                      {addedSkills > 0 ? (
                        <span className='snap-grid__added-skills'>
                          {' '}
                          (+{addedSkills} added)
                        </span>
                      ) : null}
                    </>
                  }
                />

                {humans.map((eng, hi) =>
                  teamChangeMode && onSetRating ? (
                    <GroupedTeamChangeCell
                      key={`grc-h-${eng.id}`}
                      eng={eng}
                      comp={comp}
                      ctx={ctx}
                      ratingOverrides={ratingOverrides}
                      onSetRating={onSetRating}
                      isAgent={false}
                      isDeparted={departedICIDs?.has(eng.id) ?? false}
                      gridRow={rowIdx}
                      gridColumn={layout.humanCols[hi]!}
                      availableFunctions={availableFunctions}
                      engineerSkillFunctions={engineerSkillFunctions}
                      onToggleEngineerSkillFunction={
                        onToggleEngineerSkillFunction
                      }
                    />
                  ) : (
                    <GroupedRatingCell
                      key={`grc-h-${eng.id}`}
                      eng={eng}
                      comp={comp}
                      ctx={ctx}
                      rowIsOverlay={comp.is_overlay}
                      col={layout.humanCols[hi]!}
                      row={rowIdx}
                    />
                  )
                )}
                {agents.map((eng, ai) =>
                  teamChangeMode && onSetRating ? (
                    <GroupedTeamChangeCell
                      key={`grc-a-${eng.id}`}
                      eng={eng}
                      comp={comp}
                      ctx={ctx}
                      ratingOverrides={ratingOverrides}
                      onSetRating={onSetRating}
                      isAgent
                      isDeparted={departedICIDs?.has(eng.id) ?? false}
                      gridRow={rowIdx}
                      gridColumn={layout.agentCols[ai]!}
                      availableFunctions={availableFunctions}
                      engineerSkillFunctions={engineerSkillFunctions}
                      onToggleEngineerSkillFunction={
                        onToggleEngineerSkillFunction
                      }
                    />
                  ) : (
                    <GroupedRatingCell
                      key={`grc-a-${eng.id}`}
                      eng={eng}
                      comp={comp}
                      ctx={ctx}
                      rowIsOverlay={comp.is_overlay}
                      col={layout.agentCols[ai]!}
                      row={rowIdx}
                    />
                  )
                )}
                {departed.map((eng, di) => (
                  <GroupedRatingCell
                    key={`grc-d-${eng.id}`}
                    eng={eng}
                    comp={comp}
                    ctx={ctx}
                    rowIsOverlay={comp.is_overlay}
                    col={layout.departedCols[di]!}
                    row={rowIdx}
                    extraClass='is-departed'
                  />
                ))}

                {projectGroups.flatMap((g, gi) =>
                  g.roles.map((role, ri) => {
                    const col = layout.groupRoleCols[gi]?.[ri];
                    let maxLevel = 0;
                    for (const s of comp.skills) {
                      const lvl =
                        g.requirementsByRole[role.id]?.[reqKey(comp.id, s.id)]?.level ?? 0;
                      if (lvl > maxLevel) maxLevel = lvl;
                    }
                    if (createMode && onSetRequirement && col != null) {
                      // Grouped cell in create mode: clicking sets the
                      // SAME state for every skill in the derivedContext.
                      // Treat the cell as `'deprecated'` iff every skill
                      // in the comp is marked deprecated AND no skill
                      // carries a level — otherwise level wins so a
                      // partial-deprecate render doesn't hide demand.
                      const compDeps = deprecatedMap?.[role.id] ?? {};
                      const allDeprecated =
                        comp.skills.length > 0 &&
                        comp.skills.every(s => compDeps[reqKey(comp.id, s.id)] === true) &&
                        maxLevel === 0;
                      const groupedValue: RequirementValue = allDeprecated
                        ? 'deprecated'
                        : ((maxLevel as RatingValue) || null);
                      // Function assignment: aggregate across the comp's
                      // skills. Default is each skill's catalog footprint
                      // (union across the derivedContexts it appears in — the
                      // Resilience footprint) when no skill has an explicit
                      // override, so the derivedContext cell reflects the union
                      // its skills actually reach.
                      const defaultUnion = new Set<string>();
                      for (const s of comp.skills) {
                        for (const fnId of catalogSkillFns.get(s.id) ?? []) {
                          defaultUnion.add(fnId);
                        }
                      }
                      const assignedSet = new Set<string>();
                      let anyExplicit = false;
                      for (const s of comp.skills) {
                        const explicit = skillFunctions?.[role.id]?.[reqKey(comp.id, s.id)];
                        if (explicit) {
                          anyExplicit = true;
                          for (const fnId of explicit) assignedSet.add(fnId);
                        }
                      }
                      const groupedAssigned = anyExplicit
                        ? assignedSet
                        : defaultUnion;
                      return (
                        <RequirementCell
                          key={`req-${g.id}-${role.id}`}
                          level={groupedValue}
                          isOverlay={comp.is_overlay}
                          gridRow={rowIdx}
                          gridColumn={col}
                          onSet={next => {
                            for (const s of comp.skills) {
                              onSetRequirement(role.id, comp.id, s.id, next);
                            }
                          }}
                          availableFunctions={availableFunctions}
                          assignedFunctionIds={groupedAssigned}
                          onToggleFunction={
                            onToggleSkillFunction
                              ? fnId => {
                                  for (const s of comp.skills) {
                                    onToggleSkillFunction(
                                      role.id,
                                      comp.id,
                                      s.id,
                                      fnId,
                                      [...(catalogSkillFns.get(s.id) ?? [])]
                                    );
                                  }
                                }
                              : undefined
                          }
                        />
                      );
                    }
                    if (maxLevel === 0) {
                      return (
                        <div
                          key={`req-${g.id}-${role.id}`}
                          className={`snap-cell req empty${comp.is_overlay ? ' is-overlay-cell' : ''}`}
                          style={{ gridRow: rowIdx, gridColumn: col }}
                        />
                      );
                    }
                    return (
                      <div
                        key={`req-${g.id}-${role.id}`}
                        className={`snap-cell req teal lvl-${maxLevel}${comp.is_overlay ? ' is-overlay-cell' : ''}`}
                        style={{ gridRow: rowIdx, gridColumn: col }}
                        title={`${comp.display} — strictest bar across ${comp.skills.length} skill${comp.skills.length === 1 ? '' : 's'}: ${RATING_LABELS[maxLevel as RatingValue]} (${g.display} · ${role.display})`}
                      >
                        {RATING_LETTERS[maxLevel as RatingValue]}
                      </div>
                    );
                  })
                )}
              </Fragment>
            );
          })
        : visibleComps.flatMap((comp, compIdx) => {
        const compRowStart =
          2 +
          visibleComps
            .slice(0, compIdx)
            .reduce((sum, c) => sum + c.skills.length, 0);
        const multiSkill = comp.skills.length > 1;
        return comp.skills.map((skill, i) => {
          const rowIsOverlay = comp.is_overlay || skill.is_overlay;
          const requiredLvl = maxRequirements[skill.id] ?? 0;
          const rowIdx = compRowStart + i;
          // Ungrouped mode: when the derivedContext has more than one skill,
          // let its label span 2 rows + wrap to 2 lines (same pattern
          // Resilience / Talent use). Skip rendering the col-1 cell on
          // the 2nd row so the spanning cell from row 1 occupies it.
          const skipCompCol = i === 1 && multiSkill;
          return (
            <Fragment key={`${comp.id}-${skill.id}`}>
              {skipCompCol ? null : (
                <div
                  className={`snap-grid__row-label snap-grid__row-label--muted is-frozen${
                    i === 0 && multiSkill
                      ? ' snap-grid__row-label--wrap-2'
                      : ''
                  }`}
                  style={{
                    gridRow:
                      i === 0 && multiSkill
                        ? `${rowIdx} / span 2`
                        : rowIdx,
                    gridColumn: 1,
                    left: layout.freezeLefts[0],
                  }}
                >
                  {i === 0
                    ? (() => {
                        const compSkillIds = comp.skills.map(s => s.id);
                        const isCompDeprecated =
                          compSkillIds.length > 0 &&
                          compSkillIds.every(id =>
                            deprecatedSkillIds?.has(reqKey(comp.id, id))
                          );
                        const canDeprecate =
                          !comp.is_overlay &&
                          !isCompDeprecated &&
                          onDeprecateDerivedContext != null;
                        const canRestore =
                          isCompDeprecated && onRestoreDerivedContext != null;
                        return (
                          <EditableLabel
                            text={comp.display}
                            isOverlay={comp.is_overlay}
                            isDeprecated={isCompDeprecated}
                            truncMax={multiSkill ? 60 : 15}
                            linkedPlansCount={
                              comp.is_overlay
                                ? linkedCompCounts?.get(comp.id)
                                : undefined
                            }
                            linkedNoun='derived_context'
                            onSave={
                              onRenameDerivedContext && comp.is_overlay
                                ? next => onRenameDerivedContext(comp.id, next)
                                : undefined
                            }
                            onDelete={
                              onDeleteDerivedContext && comp.is_overlay
                                ? () => onDeleteDerivedContext(comp.id)
                                : undefined
                            }
                            onDeprecate={
                              canDeprecate
                                ? () =>
                                    onDeprecateDerivedContext!(
                                      comp.id,
                                      compSkillIds
                                    )
                                : undefined
                            }
                            onRestore={
                              canRestore
                                ? () =>
                                    onRestoreDerivedContext!(
                                      comp.id,
                                      compSkillIds
                                    )
                                : undefined
                            }
                          />
                        );
                      })()
                    : null}
                </div>
              )}
              <div
                className='snap-grid__row-label snap-grid__row-label--conn is-frozen'
                style={{ gridRow: rowIdx, gridColumn: 2, left: layout.freezeLefts[1] }}
              >
                <NestConnector variant={nestVariant(i, comp.skills.length)} />
              </div>
              <div
                className='snap-grid__row-label snap-grid__row-label--prop is-frozen'
                style={{ gridRow: rowIdx, gridColumn: 3, left: layout.freezeLefts[2] }}
              >
                <PropIndicator institutional={isInstitutional(skill)} />
              </div>
              <div
                className='snap-grid__row-label is-frozen is-frozen-edge'
                style={{ gridRow: rowIdx, gridColumn: 4, left: layout.freezeLefts[3] }}
              >
                <EditableLabel
                  text={skill.display}
                  isOverlay={skill.is_overlay}
                  isDeprecated={deprecatedSkillIds?.has(reqKey(comp.id, skill.id))}
                  linkedPlansCount={
                    skill.is_overlay
                      ? linkedSkillCounts?.get(comp.id)?.get(skill.id)
                      : undefined
                  }
                  linkedNoun='skill'
                  onSave={
                    onRenameSkill && skill.is_overlay
                      ? next => onRenameSkill(comp.id, skill.id, next)
                      : undefined
                  }
                  onDelete={
                    onDeleteSkill && skill.is_overlay
                      ? () => onDeleteSkill(comp.id, skill.id)
                      : undefined
                  }
                  onRestore={
                    onRestoreSkill && deprecatedSkillIds?.has(reqKey(comp.id, skill.id))
                      ? () => onRestoreSkill(comp.id, skill.id)
                      : undefined
                  }
                />
              </div>

              {humans.map((eng, hi) =>
                teamChangeMode && onSetRating ? (
                  <TeamChangeRatingCell
                    key={`rc-h-${eng.id}`}
                    baselineRating={
                      (ctx.ratings.get(eng.id)?.get(skill.id) ??
                        null) as RatingValue | null
                    }
                    override={ratingOverrides?.[eng.id]?.[skill.id]}
                    isOverlay={rowIsOverlay}
                    isAgent={false}
                    isDeparted={departedICIDs?.has(eng.id) ?? false}
                    skillDisplay={skill.display}
                    gridRow={rowIdx}
                    gridColumn={layout.humanCols[hi]!}
                    onSet={next => onSetRating(eng.id, skill.id, next)}
                    availableFunctions={availableFunctions}
                    assignedFunctionIds={
                      new Set(
                        engineerSkillFunctions?.[eng.id]?.[skill.id] ??
                          comp.functions
                      )
                    }
                    onToggleFunction={
                      onToggleEngineerSkillFunction
                        ? fnId =>
                            onToggleEngineerSkillFunction(
                              eng.id,
                              skill.id,
                              fnId,
                              comp.functions
                            )
                        : undefined
                    }
                  />
                ) : (
                  <ResourceRatingCell
                    key={`rc-h-${eng.id}`}
                    eng={eng}
                    skillId={skill.id}
                    skillDisplay={skill.display}
                    ctx={ctx}
                    requiredLvl={requiredLvl}
                    rowIsOverlay={rowIsOverlay}
                    col={layout.humanCols[hi]!}
                    row={rowIdx}
                  />
                )
              )}
              {agents.map((eng, ai) =>
                teamChangeMode && onSetRating ? (
                  <TeamChangeRatingCell
                    key={`rc-a-${eng.id}`}
                    baselineRating={
                      (ctx.ratings.get(eng.id)?.get(skill.id) ??
                        null) as RatingValue | null
                    }
                    override={ratingOverrides?.[eng.id]?.[skill.id]}
                    isOverlay={rowIsOverlay}
                    isAgent
                    isDeparted={departedICIDs?.has(eng.id) ?? false}
                    skillDisplay={skill.display}
                    gridRow={rowIdx}
                    gridColumn={layout.agentCols[ai]!}
                    onSet={next => onSetRating(eng.id, skill.id, next)}
                    availableFunctions={availableFunctions}
                    assignedFunctionIds={
                      new Set(
                        engineerSkillFunctions?.[eng.id]?.[skill.id] ??
                          comp.functions
                      )
                    }
                    onToggleFunction={
                      onToggleEngineerSkillFunction
                        ? fnId =>
                            onToggleEngineerSkillFunction(
                              eng.id,
                              skill.id,
                              fnId,
                              comp.functions
                            )
                        : undefined
                    }
                  />
                ) : (
                  <ResourceRatingCell
                    key={`rc-a-${eng.id}`}
                    eng={eng}
                    skillId={skill.id}
                    skillDisplay={skill.display}
                    ctx={ctx}
                    requiredLvl={requiredLvl}
                    rowIsOverlay={rowIsOverlay}
                    col={layout.agentCols[ai]!}
                    row={rowIdx}
                  />
                )
              )}
              {departed.map((eng, di) => {
                const rating = ctx.ratings.get(eng.id)?.get(skill.id) ?? null;
                const cls = rating === null ? 'na' : `green lvl-${rating}`;
                return (
                  <div
                    key={`rc-d-${eng.id}`}
                    className={`snap-cell ${cls} is-departed${rowIsOverlay ? ' is-overlay-cell' : ''}`}
                    style={{ gridRow: rowIdx, gridColumn: layout.departedCols[di] }}
                  />
                );
              })}

              {projectGroups.flatMap((g, gi) =>
                g.roles.map((role, ri) => {
                  const key = reqKey(comp.id, skill.id);
                  const req = g.requirementsByRole[role.id]?.[key];
                  const col = layout.groupRoleCols[gi]?.[ri];
                  if (createMode && onSetRequirement && col != null) {
                    const isDeprecated =
                      deprecatedMap?.[role.id]?.[key] === true;
                    const flatValue: RequirementValue = isDeprecated
                      ? 'deprecated'
                      : (req?.level ?? null);
                    // Default selected functions = this skill's catalog
                    // footprint (the Resilience union), not the derivedContext's
                    // narrow functions list.
                    const defaultFnIds = [...(catalogSkillFns.get(skill.id) ?? [])];
                    const explicitFnIds =
                      skillFunctions?.[role.id]?.[key];
                    const assignedFnIds = new Set(
                      explicitFnIds ?? defaultFnIds
                    );
                    return (
                      <RequirementCell
                        key={`req-${g.id}-${role.id}`}
                        level={flatValue}
                        isOverlay={rowIsOverlay}
                        gridRow={rowIdx}
                        gridColumn={col}
                        onSet={next =>
                          onSetRequirement(role.id, comp.id, skill.id, next)
                        }
                        availableFunctions={availableFunctions}
                        assignedFunctionIds={assignedFnIds}
                        onToggleFunction={
                          onToggleSkillFunction
                            ? fnId =>
                                onToggleSkillFunction(
                                  role.id,
                                  comp.id,
                                  skill.id,
                                  fnId,
                                  defaultFnIds
                                )
                            : undefined
                        }
                      />
                    );
                  }
                  if (!req) {
                    return (
                      <div
                        key={`req-${g.id}-${role.id}`}
                        className={`snap-cell req empty${rowIsOverlay ? ' is-overlay-cell' : ''}`}
                        style={{ gridRow: rowIdx, gridColumn: col }}
                      />
                    );
                  }
                  return (
                    <div
                      key={`req-${g.id}-${role.id}`}
                      className={`snap-cell req teal lvl-${req.level}${rowIsOverlay ? ' is-overlay-cell' : ''}`}
                      style={{ gridRow: rowIdx, gridColumn: col }}
                      title={`${skill.display} — needs ${RATING_LABELS[req.level]} (${g.display} · ${role.display})`}
                    >
                      {RATING_LETTERS[req.level]}
                    </div>
                  );
                })
              )}
            </Fragment>
          );
        });
      })}

      {!hasAnyColumns ? (
        <p className='snap-subtitle' style={{ gridColumn: '1 / -1' }}>
          No team or projects to display.
        </p>
      ) : null}
    </div>
    </div>
  );
}

function ResourceColHeader({
  eng,
  col,
  row,
  zIndex,
}: {
  eng: TalentColumn;
  col: number;
  row: number;
  /** Descending left→right so the rotated name overlaps the next column. */
  zIndex?: number;
}) {
  const isAgent = getKind(eng) === 'agent';
  const isHire = isProjectedHire(eng);
  const classes = ['snap-grid__col-header'];
  if (isAgent) classes.push('snap-grid__col-header--agent');
  if (isHire) classes.push('snap-grid__col-header--hire');
  return (
    <div className={classes.join(' ')} style={{ gridRow: row, gridColumn: col, zIndex }}>
      <strong>{isAgent ? eng.display_name : formatShortName(eng.display_name)}</strong>
    </div>
  );
}

function ResourceRatingCell({
  eng,
  skillId,
  skillDisplay,
  ctx,
  requiredLvl,
  rowIsOverlay,
  col,
  row,
}: {
  eng: TalentColumn;
  skillId: string;
  skillDisplay: string;
  ctx: RoadmapProjectionContext;
  requiredLvl: number;
  rowIsOverlay: boolean;
  col: number;
  row: number;
}) {
  const rating = ctx.ratings.get(eng.id)?.get(skillId) ?? null;
  const isAgent = getKind(eng) === 'agent';
  const delta = ctx.upskillDeltas[eng.id]?.[skillId];

  const classes = ['snap-cell'];
  if (rating === null) {
    classes.push('na');
  } else {
    classes.push(`green lvl-${rating}`);
  }
  if (isAgent) classes.push('is-agent');
  if (delta) classes.push('is-upskill');
  if (rowIsOverlay) classes.push('is-overlay-cell');

  const tooltipBody: string[] = [skillDisplay];
  if (rating !== null) {
    tooltipBody.unshift(RATING_LABELS[rating as RatingValue]);
  } else if (requiredLvl > 0 && !isAgent) {
    tooltipBody.push('No rating recorded.');
  }
  if (delta) {
    const fromLabel = delta.from
      ? RATING_LABELS[delta.from as RatingValue]
      : '—';
    const toLabel = RATING_LABELS[delta.to as RatingValue];
    tooltipBody.push(`Upskill: ${fromLabel} → ${toLabel}`);
  }
  if (requiredLvl > 0 && !isAgent) {
    const ok = rating !== null && rating >= requiredLvl;
    tooltipBody.push(
      `Required: ${RATING_LABELS[requiredLvl as RatingValue]} — ${ok ? 'covered' : 'GAP'}`
    );
  }

  // CSS vars drive the upskill cell's animated fade — see
  // `.snap-cell.is-upskill` in tokens.css. CSSProperties doesn't know
  // about CSS custom properties, hence the local cast.
  const cellStyle: CSSProperties & Record<string, string | number> = {
    gridRow: row,
    gridColumn: col,
  };
  if (delta) {
    cellStyle['--upskill-from'] = GREEN_RAMP[delta.from ?? 0] ?? GREEN_RAMP[0]!;
    cellStyle['--upskill-to'] = GREEN_RAMP[delta.to] ?? GREEN_RAMP[0]!;
  }

  return (
    <div
      className={classes.join(' ')}
      style={cellStyle}
      title={tooltipBody.join(' · ')}
    >
      {delta ? <span className='snap-roadmap__upskill-mark'>Δ</span> : null}
    </div>
  );
}

/**
 * Grouped-mode cell: an engineer's BEST rating across all of a
 * derivedContext's visible skills — symmetric with the Talent view's
 * grouped cell. Tooltip lists every rated skill in the comp so the
 * single number stays auditable.
 */
function GroupedRatingCell({
  eng,
  comp,
  ctx,
  rowIsOverlay,
  col,
  row,
  extraClass,
}: {
  // Departed engineers (ProjectedDeparture) only carry id/display_name/github_login;
  // this cell only reads eng.id + the optional `kind` discriminator, so a
  // narrowed shape covers both live team columns and departed humans.
  eng: TalentColumn | { id: string };
  comp: { display: string; skills: Array<{ id: string; display: string }> };
  ctx: RoadmapProjectionContext;
  rowIsOverlay: boolean;
  col: number;
  row: number;
  extraClass?: string;
}) {
  const isAgent = 'kind' in eng && eng.kind === 'agent';
  const rated: Array<{ display: string; rating: RatingValue }> = [];
  let best: RatingValue | null = null;
  for (const skill of comp.skills) {
    const r = ctx.ratings.get(eng.id)?.get(skill.id);
    if (r === undefined) continue;
    rated.push({ display: skill.display, rating: r });
    if (best === null || r > best) best = r;
  }
  const classes = ['snap-cell', 'is-merged'];
  if (best === null) classes.push('green', 'na');
  else classes.push('green', `lvl-${best}`);
  if (isAgent) classes.push('is-agent');
  if (rowIsOverlay) classes.push('is-overlay-cell');
  if (extraClass) classes.push(extraClass);
  const tooltipBody =
    best === null
      ? `${comp.display}: no rated skills`
      : `Best in ${comp.display}: ${RATING_LABELS[best]}\n` +
        rated
          .sort((a, b) => b.rating - a.rating)
          .map(r => `${r.display} — ${RATING_LABELS[r.rating]}`)
          .join('\n');
  return (
    <div
      className={classes.join(' ')}
      style={{ gridRow: row, gridColumn: col }}
      title={tooltipBody}
    />
  );
}

/** Team-change grouped cell: editable aggregate. Effective level is the
 *  MAX over comp.skills using (override else baseline) per skill;
 *  modified iff at least one skill carries an override. Clicking a
 *  level fans out the same override to every skill in the comp,
 *  matching the requirement-picker pattern used in create mode. */
function GroupedTeamChangeCell({
  eng,
  comp,
  ctx,
  ratingOverrides,
  onSetRating,
  isAgent,
  isDeparted,
  gridRow,
  gridColumn,
  availableFunctions,
  engineerSkillFunctions,
  onToggleEngineerSkillFunction,
}: {
  eng: TalentColumn;
  comp: MergedDerivedContext;
  ctx: RoadmapProjectionContext;
  ratingOverrides: Record<string, Record<string, RatingValue>> | undefined;
  onSetRating: (
    engineerId: string,
    skillId: string,
    next: RatingValue | null
  ) => void;
  isAgent: boolean;
  isDeparted: boolean;
  gridRow: number;
  gridColumn: number;
  availableFunctions?: Array<{ id: string; display: string }>;
  engineerSkillFunctions?: Record<string, Record<string, string[]>>;
  onToggleEngineerSkillFunction?: (
    engineerId: string,
    skillId: string,
    fnId: string,
    defaultIds: string[]
  ) => void;
}) {
  const ratings = ctx.ratings.get(eng.id);
  const engOverrides = ratingOverrides?.[eng.id];
  let baselineMax = 0;
  let effectiveMax = 0;
  let anyOverride = false;
  for (const s of comp.skills) {
    const baseline = ratings?.get(s.id) ?? 0;
    if (baseline > baselineMax) baselineMax = baseline;
    const ov = engOverrides?.[s.id];
    const effective = ov !== undefined ? ov : baseline;
    if (effective > effectiveMax) effectiveMax = effective;
    if (ov !== undefined) anyOverride = true;
  }
  const baselineRating: RatingValue | null =
    baselineMax > 0 ? (baselineMax as RatingValue) : null;
  // Collapse the aggregate into the TeamChangeRatingCell's flat-cell
  // contract: pass the effective max as both baseline + override when
  // any per-skill override exists so the cell repaints with the right
  // level + dashed outline, even though the underlying overrides may
  // be heterogeneous across skills.
  const overrideForCell: RatingValue | undefined = anyOverride
    ? (effectiveMax as RatingValue)
    : undefined;
  // Aggregate function assignments across the comp's skills for the
  // "Assign functions" sub-view: a function is "checked" if it's in
  // the explicit set for ANY skill in the comp, falling back to
  // comp.functions when nothing has been overridden yet.
  const compFnDefault = comp.functions;
  const aggregatedAssigned = new Set<string>();
  let anyExplicitFn = false;
  for (const s of comp.skills) {
    const explicit = engineerSkillFunctions?.[eng.id]?.[s.id];
    if (explicit) {
      anyExplicitFn = true;
      for (const fnId of explicit) aggregatedAssigned.add(fnId);
    }
  }
  const assignedFunctionIds = anyExplicitFn
    ? aggregatedAssigned
    : new Set(compFnDefault);
  return (
    <TeamChangeRatingCell
      baselineRating={baselineRating}
      override={overrideForCell}
      isOverlay={comp.is_overlay}
      isAgent={isAgent}
      isDeparted={isDeparted}
      skillDisplay={comp.display}
      gridRow={gridRow}
      gridColumn={gridColumn}
      onSet={next => {
        for (const s of comp.skills) {
          onSetRating(eng.id, s.id, next);
        }
      }}
      availableFunctions={availableFunctions}
      assignedFunctionIds={assignedFunctionIds}
      onToggleFunction={
        onToggleEngineerSkillFunction
          ? fnId => {
              // Fan the toggle out to every skill in the comp so a
              // single grouped toggle keeps per-skill state in sync.
              for (const s of comp.skills) {
                onToggleEngineerSkillFunction(
                  eng.id,
                  s.id,
                  fnId,
                  compFnDefault
                );
              }
            }
          : undefined
      }
    />
  );
}

